import {
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import TimeAgo from "react-time-ago";
import BatteryIcon from "@mui/icons-material/BatteryFull";
import ThermometerIcon from "@mui/icons-material/Thermostat";
import TuneIcon from "@mui/icons-material/Tune";
import React from "react";
import { observer } from "mobx-react";
import AuthStore from "../stores/AuthStore";
import DeviceControlsDialog from "./DeviceControlsDialog";
import { SalesContextColor } from "../model/Order";
import CloudIcon from "@mui/icons-material/Cloud";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";

export enum PrinterStatus {
    GOOD = "GOOD",
    PAPER_EMPTY = "PAPER_EMPTY",
    PAPER_NEAR_EMPTY = "PAPER_NEAR_EMPTY",
    COVER_OPEN = "COVER_OPEN",
    PRINTER_OFFLINE = "PRINTER_OFFLINE",
    NOT_CONNECTED = "NOT_CONNECTED",
    PAPER_ERROR = "PAPER_ERROR",
    RIBBON_END = "RIBBON_END",
    BATCH_PROCESSING = "BATCH_PROCESSING",
    PRINTING = "PRINTING",
    PAUSED = "PAUSED",
    WAITING_FOR_PEELING = "WAITING_FOR_PEELING",
}

const printerStatusFriendlyName = (status: PrinterStatus): string => {
    switch (status) {
        case PrinterStatus.GOOD:
            return "Printer is running normally.";
        case PrinterStatus.PAPER_EMPTY:
            return "Printer is out of paper.";
        case PrinterStatus.PAPER_NEAR_EMPTY:
            return "Printer is running low on paper.";
        case PrinterStatus.COVER_OPEN:
            return "Printer cover is open.";
        case PrinterStatus.PRINTER_OFFLINE:
            return "Printer is offline.";
        case PrinterStatus.NOT_CONNECTED:
            return "No printer is connected.";
        case PrinterStatus.PAPER_ERROR:
            return "Printer has a paper error.";
        case PrinterStatus.RIBBON_END:
            return "Printer is out of ribbon.";
        case PrinterStatus.BATCH_PROCESSING:
            return "Printer is in the middle of a batch.";
        case PrinterStatus.PRINTING:
            return "Printer is active.";
        case PrinterStatus.PAUSED:
            return "Printer is paused.";
        case PrinterStatus.WAITING_FOR_PEELING:
            return "Printer is waiting for peeling.";
        default:
            return "Unknown printer state.";
    }
};

export const printerColorFromStatus = (status: PrinterStatus): string => {
    switch (status) {
        case PrinterStatus.GOOD:
        case PrinterStatus.BATCH_PROCESSING:
        case PrinterStatus.PRINTING:
            return "#10d15a";
        case PrinterStatus.PAPER_NEAR_EMPTY:
        case PrinterStatus.PAUSED:
        case PrinterStatus.WAITING_FOR_PEELING:
            return "#d18610";
        default:
            return "#db3232";
    }
};

export enum ThermalStatus {
    GOOD = "GOOD",
    WARM = "WARM",
    HOT = "HOT",
    CRITICAL = "CRITICAL",
    NOT_SUPPORTED = "NOT_SUPPORTED",
}

const thermalStatusFriendlyName = (status: ThermalStatus): string => {
    switch (status) {
        case ThermalStatus.GOOD:
            return "GOOD";
        case ThermalStatus.WARM:
            return "WARM";
        case ThermalStatus.HOT:
            return "HOT";
        case ThermalStatus.CRITICAL:
            return "CRITICAL";
        default:
            return "NOT SUPPORTED";
    }
};

// const thermalColorFromStatus = (status: ThermalStatus): string => {
//     switch (status) {
//         case ThermalStatus.GOOD:
//             return "#7affba";
//         case ThermalStatus.WARM:
//             return "#ffbf7a";
//         case ThermalStatus.HOT:
//             return "#ff8e7a";
//         case ThermalStatus.CRITICAL:
//             return "#ff7a7a";
//         default:
//             return "#ff7a7a";
//     }
// };

export type DeviceInfo = {
    identifier: number;
    battery: string;
    printerStatus: PrinterStatus;
    thermalStatus: ThermalStatus;
    user: string;
    updatedAt: string;
    checkedInAt: string;
    location: string;
    configuration:
        | ({
              compsEnabled: boolean;
              doubleWideTokens: boolean;
              kioskConfig: any | null;
              kioskMode: boolean;
              masterPassword: boolean;
              nodeNumber: number | null;
              offline: boolean;
              pingEnabled: boolean;
              printerEnabled: boolean;
              salesContext: "POS" | "BOXOFFICE" | "ECOMMERCE";
              serialNumber: number;
              squareEnabled: boolean;
              takeAllMode: boolean;
              timezone: string | null;
              // Reported by newer POS builds only.
              outOfService?: boolean;
              squareMode?: "READER" | "TERMINAL" | "MOBILE" | "NONE";
          } & { [lbl: string]: any })
        | null;
};

const Device = observer(
    ({
        identifier,
        battery,
        printerStatus,
        thermalStatus,
        user,
        updatedAt,
        checkedInAt,
        location,
        onUpdate,
        showLocation,
        configuration,
        onEdit,
        onSetConfig,
    }: DeviceInfo & {
        onUpdate: (request: any) => void;
        showLocation?: boolean;
        onEdit: ({ identifier, location }: { identifier: number; location: string }) => void;
        onSetConfig: ({ identifier, config }) => void;
    }) => {
        const [open, setOpen] = React.useState(false);
        const [sLocation, setSLocation] = React.useState(location);
        const [controlsOpen, setControlsOpen] = React.useState(false);

        const onLocationChange = (e: any) => {
            e.preventDefault();
            setSLocation(e.target.value);
        };

        const onCancel = () => {
            setOpen(false);
        };

        const onSave = () => {
            onUpdate({
                identifier,
                location: sLocation,
            });
            onCancel();
        };

        return (
            <Card sx={{ backgroundColor: printerColorFromStatus(printerStatus), margin: "10px" }}>
                <CardContent>
                    <Box display="flex" flexDirection="column" justifyContent="space-between" minHeight="125px">
                        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="h5" fontWeight="bold" color="black" sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                POS {identifier}{" "}
                                {AuthStore.hasAdmin && configuration && (
                                    <>
                                        <IconButton size="small" onClick={() => onSetConfig({ identifier, config: configuration })}>
                                            <QuestionMarkIcon fontSize="small" color="primary" />
                                        </IconButton>
                                        <Tooltip title={(configuration.offline ? "Offline" : "Online") + " mode"}>
                                            {configuration.offline ? <CloudOffIcon fontSize="small" /> : <CloudIcon fontSize="small" />}
                                        </Tooltip>
                                        {configuration.takeAllMode && <Chip label="Take All" size="small" color="primary" />}
                                        {configuration.outOfService && <Chip label="Out of Service" size="small" sx={{ backgroundColor: "black", color: "white" }} />}
                                        {configuration.salesContext === "BOXOFFICE" && <Chip label="Box Office" size="small" color="primary" />}
                                        {configuration.kioskMode && <Chip label="Kiosk" size="small" color="primary" />}
                                        {configuration.printerEnabled === false && <Chip label="No Printer" size="small" color="primary" />}
                                    </>
                                )}
                            </Typography>
                            <Box display="flex" flexDirection="column">
                                <Box display="flex" alignItems="center" justifyContent="flex-end">
                                    <BatteryIcon fontSize="small" sx={{ color: "black" }} />
                                    <Typography variant="h6" color="black">
                                        {parseInt(battery, 10)}%
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center">
                                    <ThermometerIcon fontSize="small" sx={{ color: "black" }} />
                                    <Typography variant="h6" color="black">
                                        {thermalStatusFriendlyName(thermalStatus)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="flex-end">
                            <Box>
                                <Typography variant="body1" color="black">
                                    Current User: {user}
                                </Typography>
                                {location && showLocation && (
                                    <Typography variant="body2" fontWeight="bolder" color="black">
                                        {location}
                                    </Typography>
                                )}
                                <Typography variant="body2" color="black">
                                    Last Checked In: <TimeAgo date={Date.parse(checkedInAt)} locale="en-US" />
                                </Typography>
                            </Box>
                            <Box>
                                {AuthStore.hasAdmin && (
                                    <React.Fragment>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => {
                                                onEdit({ identifier, location });
                                            }}
                                            sx={{ color: "black" }}
                                        >
                                            Edit
                                        </Button>
                                        <Tooltip title="Device controls">
                                            <IconButton aria-label="Device controls" onClick={() => setControlsOpen(true)}>
                                                <TuneIcon color="primary" />
                                            </IconButton>
                                        </Tooltip>
                                    </React.Fragment>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
                {AuthStore.hasAdmin && (
                    <DeviceControlsDialog
                        target={
                            controlsOpen
                                ? {
                                      kind: "device",
                                      device: {
                                          identifier,
                                          battery,
                                          printerStatus,
                                          thermalStatus,
                                          user,
                                          updatedAt,
                                          checkedInAt,
                                          location,
                                          configuration,
                                      },
                                  }
                                : null
                        }
                        onClose={() => setControlsOpen(false)}
                    />
                )}
            </Card>
        );
    }
);

export default Device;
